import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, PixelIcon } from '@/components/ui';
import { ObstacleTile } from '@/components/game';
import { FONT, FONT_SIZE, SPACING, RADIUS, COLOR, modalButtonStyle, statCardStyle } from '@/config/designTokens';
import { motion } from 'motion/react';

export interface ObstacleInfo {
  name: string;
  description: string;
  instructions: string;
  proTip: string;
  firstAppearance: number;
  icon: string;
}

export interface ObstacleTutorialModalProps {
  isOpen: boolean;
  obstacleType: string | null;
  onClose: () => void;
}

// Obstacle, powerup, and card database with PixelIcon mappings
const OBSTACLE_DATABASE: Record<string, ObstacleInfo> = {
  rock: {
    name: 'Rock',
    description: 'Solid obstacles that block movement completely.',
    instructions: 'You cannot pass through rocks. Plan your route around them.',
    proTip: 'Use rocks as strategic stopping points to control your sliding movement.',
    firstAppearance: 1,
    icon: 'rock'
  },
  lava: {
    name: 'Lava',
    description: 'Dangerous terrain that costs you a life when touched.',
    instructions: 'Avoid sliding into lava tiles. A Shield powerup can protect you once.',
    proTip: 'Use walls and stop tiles to control your slide before you reach lava.',
    firstAppearance: 16,
    icon: 'fire'
  },
  ice: {
    name: 'Ice',
    description: 'Slippery surfaces that extend your movement.',
    instructions: 'Ice tiles make you slide further than normal.',
    proTip: 'Use ice strategically to reach distant platforms or skip over obstacles.',
    firstAppearance: 25,
    icon: 'ice'
  },
  stop: {
    name: 'Stop Tile',
    description: 'Special tiles that halt your sliding movement.',
    instructions: 'Landing on a stop tile ends your slide. You stop ON the tile, not before it.',
    proTip: 'Stop tiles let you reach spots that would otherwise be inaccessible. Plan routes through them.',
    firstAppearance: 12,
    icon: 'stop'
  },
  teleporter: {
    name: 'Teleporter',
    description: 'Mystical portals that transport you instantly.',
    instructions: 'Slide onto a teleporter and you warp to its paired portal. Each portal is connected to exactly one other.',
    proTip: 'Teleporters can open shortcuts that bypass walls and obstacles entirely.',
    firstAppearance: 30,
    icon: 'portal'
  },
  enemy: {
    name: 'Enemy',
    description: 'Creatures that patrol the board and chase you.',
    instructions: 'Enemies move after each of your moves. Avoid landing on the same tile or you lose a life.',
    proTip: 'Different worlds have different enemy types: patrol, stalk, bounce, chase, and mirror. Learn their patterns!',
    firstAppearance: 5,
    icon: 'enemyBeetle'
  },
  // Card tutorials
  card_freeze: {
    name: 'Freeze Card',
    description: 'A powerful card that freezes all enemies in place.',
    instructions: 'Play this card from your hand during a level. All enemies skip their next 2 turns, giving you time to move safely.',
    proTip: 'Save Freeze for tight situations where multiple enemies are closing in.',
    firstAppearance: 0,
    icon: 'effects'
  },
  card_shield: {
    name: 'Shield Card',
    description: 'Grants you a one-hit shield against enemies or lava.',
    instructions: 'Play this card to activate a shield. The next time you would take damage, the shield absorbs it instead.',
    proTip: 'Pair Shield with aggressive routes through enemy territory to collect hard-to-reach coins.',
    firstAppearance: 0,
    icon: 'shield'
  },
  card_scout: {
    name: 'Scout Card',
    description: 'Reveals the optimal path to the exit.',
    instructions: 'Play this card to highlight the best path for your next 3 moves. Follow the glowing tiles!',
    proTip: 'Use Scout when you are stuck. It shows the shortest route but there may be alternate paths with more coins.',
    firstAppearance: 0,
    icon: 'eye'
  },
  card_rewind: {
    name: 'Rewind Card',
    description: 'Turns back time and undoes your recent moves.',
    instructions: 'Play this card to undo the last 3 moves for both you and all enemies. Great for recovering from mistakes.',
    proTip: 'Rewind is rare and valuable. Save it for moments where you are about to lose a life.',
    firstAppearance: 0,
    icon: 'undo'
  },
  card_warp: {
    name: 'Warp Card',
    description: 'Teleport instantly to any stop tile on the board.',
    instructions: 'Play this card, then tap any reachable stop tile to teleport there instantly. Bypasses walls and enemies!',
    proTip: 'Warp can skip entire sections of a level. Look for stop tiles near the exit or collectibles.',
    firstAppearance: 0,
    icon: 'portal'
  },
  // Powerup tutorials
  powerup_shield: {
    name: 'Shield Powerup',
    description: 'Protects you from one hit of damage.',
    instructions: 'Press the hotkey (1-4) to activate. While active, the next enemy hit or lava contact is absorbed. One use only.',
    proTip: 'Activate Shield before making risky moves through enemy-patrolled areas.',
    firstAppearance: 0,
    icon: 'shield'
  },
  powerup_invincibility: {
    name: 'Invincibility',
    description: 'Become immune to all damage temporarily.',
    instructions: 'Press the hotkey (1-4) to activate. While active, you can slide through enemies and lava without taking damage.',
    proTip: 'Invincibility lets you take aggressive shortcuts through lava corridors.',
    firstAppearance: 0,
    icon: 'star'
  },
  powerup_hint: {
    name: 'Path Hint',
    description: 'Shows the optimal path to the exit.',
    instructions: 'Press the hotkey (1-4) to activate. Glowing tiles show you the best route for the next few moves.',
    proTip: 'Use hints sparingly on harder levels. Try to solve easier levels without them for more satisfaction!',
    firstAppearance: 0,
    icon: 'eye'
  },
  powerup_undo: {
    name: 'Undo Moves',
    description: 'Rewinds your last 3 moves.',
    instructions: 'Press the hotkey (1-4) to undo your recent moves. Both you and enemies return to their previous positions.',
    proTip: 'Undo is perfect for those "I should not have gone that way" moments.',
    firstAppearance: 0,
    icon: 'undo'
  },
};

const ObstacleTutorialModal: React.FC<ObstacleTutorialModalProps> = ({
  isOpen,
  obstacleType,
  onClose
}) => {
  const obstacleInfo = obstacleType ? OBSTACLE_DATABASE[obstacleType] : null;
  const [countdown, setCountdown] = useState(8);

  // Cache last valid obstacle data so content stays visible during Modal exit animation.
  const lastObstacleInfoRef = useRef(obstacleInfo);
  const lastObstacleTypeRef = useRef(obstacleType);
  if (obstacleInfo) lastObstacleInfoRef.current = obstacleInfo;
  if (obstacleType) lastObstacleTypeRef.current = obstacleType;
  const displayInfo = obstacleInfo || lastObstacleInfoRef.current;
  const displayType = obstacleType || lastObstacleTypeRef.current;

  // Keep a stable ref to onClose so the countdown interval never restarts
  // due to the parent re-rendering with a new function reference.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const stableClose = useCallback(() => onCloseRef.current(), []);

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen && obstacleInfo) {
      setCountdown(8);
    }
  }, [isOpen, obstacleInfo]);

  // Auto-close countdown timer
  useEffect(() => {
    if (!isOpen || !obstacleInfo) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Call stableClose outside the state updater to avoid
          // "Cannot update a component while rendering" warning.
          setTimeout(() => stableClose(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, stableClose, obstacleInfo]);

  // Keyboard handler for escape
  useEffect(() => {
    if (!isOpen || !obstacleInfo) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        stableClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, stableClose, obstacleInfo]);

  // Don't render if we've never had obstacle info
  if (!displayInfo || !displayType) return null;

  const renderHeader = () => (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        textAlign: 'center',
        marginBottom: SPACING.lg,
      }}
    >
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: SPACING.sm,
          marginBottom: SPACING.md,
        }}
      >
        <div style={{
          padding: SPACING.md,
          backgroundColor: `color-mix(in srgb, ${COLOR.statBlue} 10%, transparent)`,
          borderRadius: RADIUS.xl,
          border: `3px solid color-mix(in srgb, ${COLOR.statBlue} 30%, transparent)`,
        }}>
          <ObstacleTile type={displayType as any} size={56} />
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, type: "spring" }}
          style={{
            padding: SPACING.sm,
            backgroundColor: COLOR.statBlue,
            borderRadius: RADIUS.full,
            border: '2px solid #ffffff',
            alignSelf: 'flex-start',
          }}
        >
          <PixelIcon name="info" size={18} color="#ffffff" />
        </motion.div>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        style={{
          fontSize: FONT_SIZE.modalTitle,
          fontWeight: 'bold',
          color: COLOR.statBlue,
          margin: '0',
          fontFamily: FONT.heading,
          textShadow: '1px 1px 0 #3182ce',
        }}
      >
        {displayType?.startsWith('card_') ? 'New Card' : displayType?.startsWith('powerup_') ? 'New Powerup' : 'New Obstacle'}: {displayInfo.name}
      </motion.h1>
    </motion.div>
  );

  const sectionHeadingStyle = (color: string): React.CSSProperties => ({
    fontSize: FONT_SIZE.sectionHeading,
    fontWeight: 'bold',
    color,
    margin: `0 0 ${SPACING.sm} 0`,
    fontFamily: FONT.heading,
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.sm,
  });

  const sectionBodyStyle: React.CSSProperties = {
    fontSize: FONT_SIZE.body,
    color: COLOR.textPrimary,
    margin: 0,
    lineHeight: 1.5,
    fontFamily: FONT.body,
  };

  const renderContent = () => (
    <div style={{
      display: 'grid',
      gap: SPACING.md,
      marginBottom: SPACING.lg,
    }}>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.6 }}
        style={statCardStyle(COLOR.successLight)}
      >
        <h3 style={sectionHeadingStyle(COLOR.successLight)}>
          <PixelIcon name="info" size={16} color={COLOR.successLight} />
          Description
        </h3>
        <p style={sectionBodyStyle}>
          {displayInfo.description}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8 }}
        style={statCardStyle(COLOR.warningLight)}
      >
        <h3 style={sectionHeadingStyle(COLOR.warningLight)}>
          <PixelIcon name="settings" size={16} color={COLOR.warningLight} />
          How to Handle
        </h3>
        <p style={sectionBodyStyle}>
          {displayInfo.instructions}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        style={{
          ...statCardStyle(COLOR.statYellow),
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Sparkle decoration */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 360],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: SPACING.sm,
            right: SPACING.md,
          }}
        >
          <PixelIcon name="star" size={14} color={COLOR.statYellow} />
        </motion.div>

        <h3 style={sectionHeadingStyle(COLOR.statYellow)}>
          <PixelIcon name="lightbulb" size={16} color={COLOR.statYellow} />
          Pro Tip
        </h3>
        <p style={sectionBodyStyle}>
          {displayInfo.proTip}
        </p>
      </motion.div>
    </div>
  );

  const renderFooter = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: SPACING.md,
    }}>
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        onClick={(e) => {
          e.stopPropagation();
          stableClose();
        }}
        style={{
          ...modalButtonStyle(COLOR.info),
          minWidth: '180px',
          position: 'relative',
          zIndex: 10,
          fontFamily: FONT.heading,
        }}
        whileHover={{ backgroundColor: COLOR.infoBorder, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <PixelIcon name="success" size={20} color="#ffffff" />
        <span>Got it!</span>
      </motion.button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        style={{
          textAlign: 'center',
          padding: `${SPACING.sm} ${SPACING.lg}`,
          backgroundColor: COLOR.surfaceMuted,
          borderRadius: RADIUS.sm,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: SPACING.sm,
        }}
      >
        <p style={{
          fontSize: FONT_SIZE.countdownLabel,
          color: COLOR.textSecondary,
          margin: 0,
          fontFamily: FONT.body,
        }}>
          Auto-closes in
        </p>
        <motion.div
          key={countdown}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          style={{
            fontSize: FONT_SIZE.sectionHeading,
            fontWeight: 'bold',
            color: countdown <= 3 ? COLOR.dangerLight : COLOR.statBlue,
            fontFamily: FONT.heading,
          }}
        >
          {countdown}s
        </motion.div>
      </motion.div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={stableClose}
      variant="info"
      size="medium"
      showCloseButton={false}
      closeOnBackdrop={true}
      closeOnEscape={true}
    >
      {renderHeader()}
      {renderContent()}
      {renderFooter()}
    </Modal>
  );
};

// Function to check if obstacle tutorial should be shown
export const shouldShowObstacleTutorial = (obstacleType: string, currentLevel: number): boolean => {
  const obstacleInfo = OBSTACLE_DATABASE[obstacleType];
  if (!obstacleInfo) return false;

  // Check if this is the first appearance level
  if (currentLevel < obstacleInfo.firstAppearance) return false;

  // Check if tutorial was already shown
  const tutorialKey = `obstacle_tutorial_${obstacleType}`;
  const wasShown = localStorage.getItem(tutorialKey);
  
  return !wasShown;
};

// Function to mark obstacle tutorial as shown
export const markObstacleTutorialShown = (obstacleType: string) => {
  const tutorialKey = `obstacle_tutorial_${obstacleType}`;
  localStorage.setItem(tutorialKey, 'true');
};

export default ObstacleTutorialModal;